import { DRI_SUPPLY_SECTION } from "./parameterSections";
import { resolvePeriodLayout } from "./periodLayout";
import {
  Alert,
  Box,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ReactNode, useState } from "react";
import dayjs from "dayjs";
import { useTenantData } from "contexts/tenant";
import { useTenant } from "hooks/settings";
import { useFormatNumber } from "src/utils/formatNumber";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import {
  AutoRecipeParameterDefinition,
  AutoRecipeParameters,
  AutoRecipeParameterScope,
  useGetMaterialPricesQuery,
} from "store/api/generatedApi";
import {
  ParameterValidation,
  nullableSchema,
  parameterFieldSchema,
  scalarSchema,
} from "./parameterSchema";
import {
  MonthlyTargetsEditor,
  TimeIntervalsEditor,
} from "./ParameterCollections";

import { PeriodWeightsPlot } from "./PeriodWeightsPlot";

import { RuleParameterEditor, ruleWidgets } from "./RuleParameterEditor";

import {
  ConditionalRulesEditor,
  isConditionalWidget,
} from "./ConditionalRulesEditor";

type ParameterName = string;

export const AutoRecipeParametersForm = ({
  parameters,
  definition,
  onChange,
  disabled,
  scope,
  validation,
  supplyPreview,
}: {
  parameters: AutoRecipeParameters;
  definition: AutoRecipeParameterDefinition;
  onChange: (parameters: AutoRecipeParameters) => void;
  disabled: boolean;
  scope: AutoRecipeParameterScope;
  validation: ParameterValidation;
  supplyPreview?: ReactNode;
}) => {
  const { t } = useTenantTranslation();
  const periodLayout = resolvePeriodLayout({
    layout: definition.period_layout,
    parameters,
  });
  const tenantName = useTenant();
  const formatPrice = useFormatNumber()(2);
  const [priceTimestamp] = useState(() => dayjs().toISOString());
  const needsMaterialPrices = definition.sections.some((section) =>
    section.fields.some(
      (field) =>
        field.scopes.includes(scope) && typeof field.price_material === "string"
    )
  );
  const materialPrices = useGetMaterialPricesQuery(
    { tenantName, timestamp: priceTimestamp },
    { skip: !needsMaterialPrices, refetchOnFocus: true }
  );
  const units = useUnitsFormatter(false);
  const { local_timezone: timezone } = useTenantData();
  const { valid, invalidPaths, supported } = validation;
  if (!supported)
    return <Alert severity="error">{t("autoUnsupportedParameters")}</Alert>;
  return (
    <Stack gap={3}>
      {!valid ? (
        <Alert severity="error">{t("autoInvalidParameters")}</Alert>
      ) : null}
      {definition.sections.map((section) => {
        const sectionValues: unknown = parameters[section.name];
        if (
          sectionValues === null ||
          typeof sectionValues !== "object" ||
          Array.isArray(sectionValues)
        )
          return null;
        const fields = section.fields.filter(
          (field) =>
            field.scopes.includes(scope) && !isConditionalWidget(field.widget)
        );
        if (fields.length === 0) return null;
        const sectionLabel = section.translation_key
          ? t(section.translation_key, { defaultValue: section.label })
          : section.label;
        const values = sectionValues as Record<ParameterName, unknown>;
        return (
          <Stack key={section.name} gap={2}>
            <Typography variant="h6">{sectionLabel}</Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  sm: "repeat(2, minmax(0, 1fr))",
                },
                gap: 2,
                "& > *": { minWidth: 0 },
              }}
            >
              {fields.map((field) => {
                const schema = parameterFieldSchema({
                  definition,
                  section: section.name,
                  field: field.name,
                });
                const scalar = scalarSchema(schema);
                const nullable = nullableSchema(schema);
                const amount = values[field.name];
                const materialPrice = field.price_material
                  ? materialPrices.currentData?.[field.price_material]
                  : undefined;
                const text = field.translation_key
                  ? t(field.translation_key, { defaultValue: field.label })
                  : field.label;
                const unit = field.unit ? units(field.unit, false) : "";
                const label = unit ? `${text} (${unit})` : text;
                const onFieldChange = (value: unknown) => {
                  onChange({
                    ...parameters,
                    [section.name]: { ...values, [field.name]: value },
                  });
                };
                const fieldInvalidPaths = invalidPaths
                  .filter(
                    (path) => path[0] === section.name && path[1] === field.name
                  )
                  .map((path) => path.slice(2));
                if (
                  field.widget === "time_intervals" ||
                  field.widget === "monthly_targets"
                )
                  return (
                    <Stack
                      key={field.name}
                      gap={2}
                      sx={{ gridColumn: "1 / -1" }}
                    >
                      {label !== sectionLabel ? (
                        <Typography variant="subtitle1">{label}</Typography>
                      ) : null}
                      {field.help_text || field.help_translation_key ? (
                        <Typography variant="body2" color="text.secondary">
                          {field.help_translation_key
                            ? t(field.help_translation_key, {
                                defaultValue: field.help_text ?? "",
                              })
                            : field.help_text}
                        </Typography>
                      ) : null}
                      {field.widget === "time_intervals" ? (
                        <TimeIntervalsEditor
                          value={amount}
                          onChange={onFieldChange}
                          disabled={disabled}
                          invalidPaths={fieldInvalidPaths}
                        />
                      ) : (
                        <MonthlyTargetsEditor
                          mode={field.monthly_value_mode ?? "independent"}
                          value={amount}
                          onChange={onFieldChange}
                          disabled={disabled}
                          invalidPaths={fieldInvalidPaths}
                          schema={schema}
                          label={label}
                          initialValue={
                            field.initial_value_from
                              ? values[field.initial_value_from]
                              : undefined
                          }
                        />
                      )}
                    </Stack>
                  );
                if (ruleWidgets.some((widget) => widget === field.widget))
                  return (
                    <Stack
                      key={field.name}
                      gap={1}
                      sx={{ gridColumn: "1 / -1" }}
                    >
                      {label !== sectionLabel &&
                      field.widget !== "steel_grades" ? (
                        <Typography variant="subtitle1">{label}</Typography>
                      ) : null}
                      <RuleParameterEditor
                        widget={field.widget}
                        value={amount}
                        onChange={onFieldChange}
                        disabled={disabled}
                        periodLayout={periodLayout}
                      />
                    </Stack>
                  );
                const numeric =
                  scalar.type === "number" || scalar.type === "integer";
                const dateTime = scalar.format === "date-time";
                const month = field.widget === "month";
                const displayed =
                  month && typeof amount === "string"
                    ? dayjs(amount).format("YYYY-MM")
                    : numeric
                      ? typeof amount === "number" && Number.isFinite(amount)
                        ? amount
                        : ""
                      : dateTime &&
                          typeof amount === "string" &&
                          dayjs(amount).isValid()
                        ? dayjs(amount).tz(timezone).format("YYYY-MM-DDTHH:mm")
                        : typeof amount === "string" ||
                            typeof amount === "number"
                          ? amount
                          : "";
                return (
                  <TextField
                    key={field.name}
                    label={label}
                    disabled={disabled}
                    type={
                      month
                        ? "month"
                        : numeric
                          ? "number"
                          : dateTime
                            ? "datetime-local"
                            : scalar.format === "date"
                              ? "date"
                              : "text"
                    }
                    select={scalar.enum !== undefined}
                    value={displayed}
                    placeholder={
                      typeof materialPrice === "number" &&
                      Number.isFinite(materialPrice)
                        ? formatPrice(materialPrice)
                        : undefined
                    }
                    error={fieldInvalidPaths.length > 0}
                    helperText={
                      field.help_translation_key
                        ? t(field.help_translation_key, {
                            defaultValue: field.help_text ?? "",
                          })
                        : field.help_text
                    }
                    slotProps={{
                      inputLabel: { shrink: true },
                      htmlInput: {
                        min: scalar.minimum ?? scalar.exclusiveMinimum,
                        max: scalar.maximum ?? scalar.exclusiveMaximum,
                        step: scalar.type === "integer" ? 1 : "any",
                      },
                    }}
                    onChange={(event) => {
                      const entered = event.target.value;
                      onFieldChange(
                        entered === ""
                          ? nullable
                            ? null
                            : numeric
                              ? Number.NaN
                              : ""
                          : month
                            ? `${entered}-01`
                            : numeric
                              ? Number(entered)
                              : dateTime
                                ? dayjs.tz(entered, timezone).toISOString()
                                : entered
                      );
                    }}
                  >
                    {nullable && scalar.enum ? (
                      <MenuItem value="">{t("none")}</MenuItem>
                    ) : null}
                    {scalar.enum?.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                );
              })}
            </Box>
            {section.name === DRI_SUPPLY_SECTION &&
            parameters[DRI_SUPPLY_SECTION]
              ? supplyPreview
              : null}
            {section.name === "period_weights" ? (
              <PeriodWeightsPlot
                parameters={sectionValues}
                periodLayout={periodLayout}
              />
            ) : null}
          </Stack>
        );
      })}
      <ConditionalRulesEditor
        periodLayout={periodLayout}
        parameters={parameters}
        definition={definition}
        scope={scope}
        onChange={onChange}
        disabled={disabled}
      />
    </Stack>
  );
};
